import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Truck } from "lucide-react";

import { formatUyMinor } from "@/lib/formatUyMinor";
import { previewQuoteDelivery } from "@/services/quoteRequestsService";
import { listUserAddresses } from "@/services/userAddressesService";
import { Card, CardContent } from "@/components/ui/card";
import {
  AddressPicker,
  addressSelectionBlocker,
  type AddressSelection,
} from "@/components/shared/AddressPicker";

export interface QuoteDeliveryAddressStepProps {
  selection: AddressSelection;
  onSelectionChange: (next: AddressSelection) => void;
  disabled?: boolean;
  /**
   * Raised when the chosen address cannot be priced (control C-3), so the
   * footer's Continue can block on it.
   *
   * Lifted rather than duplicated: the page would otherwise need its own copy
   * of the zone query to decide the same thing, and two copies of "can we
   * price this" is how a button ends up enabled while the panel above it says
   * we cannot ship there.
   */
  onPriceabilityChange?: (unpriceable: boolean) => void;
  /**
   * Renders the no-Uruguayan-address escape, and reports it.
   *
   * OMITTED BY THE AWARD DIALOG, deliberately. There the consumer is accepting
   * a quote for work that ends in paper, so "I have no address" is not an
   * answer — the whole point of asking at that moment is that a destination
   * has become necessary. The escape belongs only where the question is still
   * hypothetical, which is the wizard.
   */
  optedOut?: boolean;
  onOptedOutChange?: (next: boolean) => void;
}

/**
 * Where the physical document goes (spec 8c2aab49 D3; reworked by e370e36f).
 *
 * SHOWN TO EVERY CONSUMER since fccc76f1 — the earlier "only when intake said
 * paper" gate was the defect, because triage is an AI guess made before anyone
 * qualified has read the trámite while physicality is the professional's call
 * at quote time. It is a phase rather than a block on the summary because the
 * address determines the shipping price, so it has to be settled before a
 * screen that states a total.
 *
 * ALSO USED BY THE AWARD DIALOG, for a request that reached award with no
 * destination. Same picker, same refusal, same copy — a second address form
 * would be a second set of rules to drift from these.
 *
 * Deliberately thin. `AddressPicker` already does saved-addresses, Google
 * Places entry, contact fields and courier notes, and `addressSelectionBlocker`
 * already names the reason a selection is unusable — rebuilding any of that
 * here would be a second set of rules to drift from the first.
 */
export function QuoteDeliveryAddressStep({
  selection,
  onSelectionChange,
  disabled = false,
  onPriceabilityChange,
  optedOut = false,
  onOptedOutChange,
}: QuoteDeliveryAddressStepProps): JSX.Element {
  const { t } = useTranslation();
  // The blocker, not a boolean: a step whose button is disabled without
  // saying why is a dead end, and the picker's own helper already knows
  // whether the gap is the address or the phone.
  const blocker = addressSelectionBlocker(selection);

  // The zone lives on the ADDRESS, and a saved selection carries only its id —
  // so the saved rows are read to find the one picked. Cheap and already
  // cached by the picker's own list query under the same key.
  const savedQuery = useQuery({
    queryKey: ["me", "addresses"],
    queryFn: listUserAddresses,
    enabled: selection?.kind === "saved",
  });

  const zoneFields =
    selection?.kind === "new"
      ? {
          adminArea: selection.payload.adminArea ?? null,
          countryCode: selection.payload.countryCode ?? null,
        }
      : selection?.kind === "saved"
        ? (() => {
            const row = savedQuery.data?.items.find((a) => a.id === selection.addressId);
            return row ? { adminArea: row.adminArea ?? null, countryCode: row.countryCode ?? null } : null;
          })()
        : null;

  // Priced by the SERVER, not here: the tiers are admin-editable config rows,
  // and a client-side copy would quote a number the checkout then contradicts.
  const previewQuery = useQuery({
    queryKey: ["quote-delivery-preview", zoneFields?.adminArea, zoneFields?.countryCode],
    queryFn: () => previewQuoteDelivery(zoneFields ?? {}),
    enabled: zoneFields != null && Boolean(zoneFields.adminArea),
  });
  const preview = previewQuery.data;
  // Distinguished from "not loaded yet" on purpose: a resolved response whose
  // zone is null is the C-3 refusal, and the consumer has to see that rather
  // than an empty space where a price should be.
  const unpriceable = preview != null && preview.zone == null;

  useEffect(() => {
    onPriceabilityChange?.(unpriceable);
  }, [unpriceable, onPriceabilityChange]);

  return (
    <div className="space-y-4">
      <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35">
        <CardContent className="space-y-4 pt-6">
          <div className="flex gap-2.5">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <div className="max-w-[68ch] space-y-1">
              <h2 className="text-[0.9375rem] font-semibold leading-6 text-foreground">
                {t("requests.user.quoteDeliveryStepTitle")}
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {t("requests.user.quoteDeliveryStepHint")}
              </p>
            </div>
          </div>

          <AddressPicker
            selection={selection}
            onSelectionChange={onSelectionChange}
            disabled={disabled || optedOut}
            showContactFields
            showCourierNotesField
            idPrefix="quote-delivery"
          />

          {onOptedOutChange ? (
            /*
              THE ESCAPE, and it is one day old as a necessity. Since the
              address step became unconditional AND mandatory, and zones
              resolve only within Uruguay, a consumer abroad could not file a
              request at all — a quoting inconvenience had become a signup
              block. Stated as something the consumer SAYS rather than a
              skippable step, because the answer is recorded on the request and
              changes what professionals are told.
            */
            <label className="flex items-start gap-2.5 rounded-md border border-border/80 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                checked={optedOut}
                disabled={disabled}
                onChange={(e) => onOptedOutChange(e.target.checked)}
              />
              <span className="space-y-0.5">
                <span className="block text-sm font-medium text-foreground">
                  {t("requests.user.quoteDeliveryNoAddressLabel")}
                </span>
                <span className="block text-xs leading-relaxed text-muted-foreground">
                  {t("requests.user.quoteDeliveryNoAddressHint")}
                </span>
              </span>
            </label>
          ) : null}

          {optedOut ? null : blocker ? (
            <p className="text-xs text-muted-foreground">
              {t(
                blocker === "contactPhone"
                  ? "requests.user.quoteDeliveryNeedsPhone"
                  : "requests.user.quoteDeliveryNeedsAddress",
              )}
            </p>
          ) : unpriceable ? (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-foreground">
              <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-destructive" aria-hidden />
              <span>{t("requests.user.quoteDeliveryUnpriceable")}</span>
            </p>
          ) : preview && preview.feeMinor > 0 ? (
            <p className="flex items-baseline justify-between gap-4 border-t border-border/60 pt-3 text-sm">
              <span className="text-muted-foreground">
                {t(`requests.user.quoteDeliveryZone_${preview.zone}`, {
                  defaultValue: t("requests.user.quoteDeliveryZoneGeneric"),
                })}
              </span>
              <span className="font-semibold tabular-nums text-foreground">
                {formatUyMinor(preview.feeMinor)}
              </span>
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
